import {
  Box,
  Button,
  Divider,
  FormErrorMessage,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'ui-components';
import clientApi from '~/clientApi';
import { Creator } from '~/clientApi/creator.model';
import { ConfirmDiscardChangesOnLeaveModal } from '~/components/ConfirmDiscardChangesOnLeaveModal';
import Modal from '~/components/Modal/Modal';
import {
  METRICS,
  SNACKBAR_ERROR_GENERIC_OPTIONS,
  SOCIAL_PLATFORMS,
  SOCIAL_PLATFORMS_NAMES,
} from '~/constants';
import { MEDIA_PACK_ENTITY_QUERY_KEY } from '~/queries/keys';
import { selectCreator } from '~/selectors/creator';
import {
  CreatorBenchmarks,
  DefaultMediaPackType,
  GenericIndexSignature,
  MediaPackBenchmarkType,
  TMediaPackPosition,
} from '~/types';
import { getCreatorPlatformConnectionStatus } from '~/utils';

import { MediaPackDetailEditHighlightsModalComponent } from './MediaPackDetailEditHighlightsModal';
import {
  METRIC_CONFIG,
  PLATFORM_METRICS,
} from './MediaPackDetailEditHighlightsModal.constants';
import {
  changeBenchmark,
  createDefaultLayout,
} from './MediaPackDetailEditHighlightsModal.helpers';
import { IMetricPositionUpdateMutationContext } from './MediaPackDetailEditHighlightsModal.types';
import {
  TValidateErrors,
  validate,
} from './MediaPackDetailEditHighlightsModal.validators';

interface Props {
  benchmarks: CreatorBenchmarks;
  isOpen: boolean;
  mediaPack: GenericIndexSignature<DefaultMediaPackType>;
  onClose: () => void;
  platform: SOCIAL_PLATFORMS;
}

// TODO: Remove this function and all hacky mappings below when the backend is updated https://whalar.atlassian.net/browse/FOAM-12484
const mapMetrics = (metrics: string[]) =>
  metrics.map((metric) => {
    switch (metric) {
      case 'igAvgFeedFollowerEngRate':
        return 'igAvgFeedReelsFollowerEngRate';
      case 'igAvgFeedEngagements':
        return 'igAvgFeedReelsEngagements';
      case 'igAvgFeedImpressions':
        return 'igAvgFeedStoryImpressions';
      case 'igAvgFeedReach':
        return 'igAvgFeedReelsStoryReach';
      case 'igAvgFeedReachEngRate':
        return 'igAvgFeedReelsReachEngRate';
      default:
        return metric;
    }
  });

// @ts-ignore
const mapTempMetricsPosition = (metricsPosition) => {
  return {
    ...metricsPosition,
    instagram: {
      metric1: mapMetrics(metricsPosition.instagram.metric1),
      metric2: mapMetrics(metricsPosition.instagram.metric2),
      metric3: mapMetrics(metricsPosition.instagram.metric3),
    },
  };
};

export const MediaPackDetailEditHighlightsModal: React.FC<Props> = ({
  benchmarks,
  isOpen,
  mediaPack,
  onClose,
  platform,
}) => {
  const creator = useSelector(selectCreator);
  const platformStatusConnection = getCreatorPlatformConnectionStatus(
    creator as Creator,
  )[platform];
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<TValidateErrors>({});
  const [metricActive, setMetricActive] = useState<METRICS>(METRICS.metric1);
  const [metricsPosition, setMetricsPosition] = useState(
    createDefaultLayout({
      platform,
      position: mapTempMetricsPosition(mediaPack.layout?.metricsPosition),
    }),
  );

  const {
    isOpen: isDiscardModalOpen,
    onClose: onDiscardModalClose,
    onOpen: onDiscardModalOpen,
  } = useDisclosure();
  const metrics = PLATFORM_METRICS[platform];
  const metricsConfig = METRIC_CONFIG[platform];
  const hasChanges = !isEqual(
    mediaPack.layout?.metricsPosition?.[platform],
    metricsPosition,
  );
  const snackbar = useSnackbar();

  const updateMetricsPositionMutation = useMutation<
    DefaultMediaPackType,
    TMediaPackPosition,
    unknown,
    IMetricPositionUpdateMutationContext
  >({
    mutationFn: async (metricsPosition) =>
      // setIsSubmitting(true);
      clientApi.mediaPacks.update({
        id: mediaPack.id,
        layout: { ...mediaPack.layout, metricsPosition },
        _impersonateOnce: creator?.id,
      }),
    onMutate: (metricsPosition) => {
      const previousMetricsPosition = { ...mediaPack.layout.metricsPosition };

      queryClient.setQueryData([MEDIA_PACK_ENTITY_QUERY_KEY, mediaPack.id], {
        ...mediaPack,
        layout: {
          ...mediaPack.layout,
          metricsPosition,
        },
      });

      return { previousMetricsPosition };
    },
    onError: (_error, _, context) => {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
      queryClient.setQueryData([MEDIA_PACK_ENTITY_QUERY_KEY, mediaPack.id], {
        ...mediaPack,
        metricsPosition: context?.previousMetricsPosition,
      });
    },
    onSettled: () => {
      onClose();

      return queryClient.invalidateQueries({
        queryKey: [MEDIA_PACK_ENTITY_QUERY_KEY, mediaPack.id],
      });
    },
  });

  const handleSubmit = () => {
    updateMetricsPositionMutation.mutate({
      ...mediaPack.layout.metricsPosition,
      [platform]: metricsPosition,
    });
  };
  const handleClickMetric = (metric: METRICS) => setMetricActive(metric);
  const handleChangeBenchmark = (benchmark: MediaPackBenchmarkType) => {
    const metricConfig = metricsConfig[metricActive];
    const updatedMetricsPosition = { ...metricsPosition };

    updatedMetricsPosition[metricActive] = changeBenchmark({
      benchmark,
      metricActive,
      metricConfig,
      metricsPosition,
    });

    setMetricsPosition(updatedMetricsPosition);
  };
  const handleCancel = () => {
    if (hasChanges) {
      onDiscardModalOpen();
    } else {
      onClose();
    }
  };
  const handleDiscard = () => {
    onDiscardModalClose();
    onClose();
  };

  useEffect(() => {
    const errors = validate(metricsPosition, platform);
    setErrors(errors);
  }, [metricsPosition, platform]);

  useEffect(() => {
    if (isOpen) {
      const updatedMetricsPosition = createDefaultLayout({
        platform,
        position: mapTempMetricsPosition(mediaPack.layout?.metricsPosition),
      });

      setMetricActive(METRICS.metric1);

      if (!isEqual(metricsPosition, updatedMetricsPosition)) {
        setMetricsPosition(updatedMetricsPosition);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, isOpen]);

  return (
    <>
      <Modal
        closeOnEsc
        closeOnOverlayClick
        isOpen={isOpen}
        onClose={handleCancel}
        size="4xl"
        modalContentProps={{
          maxHeight: '100%',
        }}
      >
        <Modal.Header>
          <Text fontSize="xl" fontWeight={500} marginBottom={4}>
            Choose your {SOCIAL_PLATFORMS_NAMES[platform]} highlights:
          </Text>
          <Divider width="auto" />
        </Modal.Header>

        <Modal.CloseButton />

        <Modal.Body
          paddingTop={0}
          paddingBottom={0}
          paddingX={6}
          overflowY="auto"
          maxHeight="100%"
        >
          <MediaPackDetailEditHighlightsModalComponent
            benchmarks={benchmarks}
            errors={errors}
            metrics={metrics}
            metricActive={metricActive}
            metricsConfig={metricsConfig}
            metricsPosition={metricsPosition}
            onClickMetric={handleClickMetric}
            onChangeBenchmark={handleChangeBenchmark}
            platformConnection={platformStatusConnection}
          />
        </Modal.Body>

        <Box paddingX={3}>
          <Modal.Footer paddingX={3} justifyContent="space-between">
            <Button onClick={handleCancel} variant="link" type="button">
              Cancel
            </Button>

            <FormErrorMessage data-testid={`error-${metricActive}`} />

            <Button
              borderRadius="full"
              colorScheme="secondary"
              isDisabled={!hasChanges || !!Object.keys(errors).length}
              isLoading={updateMetricsPositionMutation.isLoading}
              onClick={handleSubmit}
            >
              Save all changes
            </Button>
          </Modal.Footer>
        </Box>
      </Modal>

      <ConfirmDiscardChangesOnLeaveModal
        isOpen={isDiscardModalOpen}
        onClose={onDiscardModalClose}
        onConfirm={handleDiscard}
      />
    </>
  );
};
